import { Box, Link, ThemeProvider } from "@mui/material";
import Footer from "app/components/Footer";
import Header from "app/components/Header";
import ResponseSnackbar from "app/components/ResponseSnackbar";
import useSendPlanInquiryMail from "app/hooks/mail/useSendPlanInquiryMail";
import useAuth from "app/hooks/useAuth";
import { AppRoutes, pricingModel } from "app/utils/global_constants";
import { useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import AudienceSection from "./components/AudienceSection";
import CtaSection from "./components/CtaSection";
import FeaturesSection from "./components/FeaturesSection";
import HeroSection from "./components/HeroSection";
import PlanInquiryDialog from "./components/PlanInquiryDialog";
import PricingSection from "./components/PricingSection";
import ProductsSection from "./components/ProductsSection";
import TestimonialsSection from "./components/TestimonialsSection";
import WorkflowSection from "./components/WorkflowSection";
import landingTheme from "./theme";

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pricingAudience, setPricingAudience] = useState("candidate");
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showPlanInquiryDialog, setShowPlanInquiryDialog] = useState(false);
  const [response, setResponse] = useState({
    message: "",
    severity: "success",
    open: false
  });

  const startHiring = () => {
    navigate(user?.role === "company" ? AppRoutes.ROUTE_POST_JOB : AppRoutes.ROUTE_LOGIN_COMPANY);
  };

  const findJobs = () => navigate("/jobs");
  const openCandidateLogin = () => navigate(AppRoutes.ROUTE_LOGIN_CANDIDATE);

  const closePlanInquiryDialog = () => {
    setShowPlanInquiryDialog(false);
    setSelectedPlan(null);
  };

  const openPlanInquiryDialog = (plan) => {
    setSelectedPlan(plan);
    setShowPlanInquiryDialog(true);
  };

  const { mutate: sendInquiryMail } = useSendPlanInquiryMail({
    onSuccess: () => {
      setResponse({
        message: "Inquiry sent successfully!",
        severity: "success",
        open: true
      });
      closePlanInquiryDialog();
    },
    onError: () => {
      setResponse({
        message: "Failed to send inquiry. Please try again later.",
        severity: "error",
        open: true
      });
    }
  });

  const selectedPricingModel = pricingModel[pricingAudience];

  return (
    <ThemeProvider theme={landingTheme}>
      <Helmet>
        <title>NextHire | AI Recruitment Platform</title>
        <meta
          name="description"
          content="NextHire connects hiring teams and candidates through intelligent recruitment technology."
        />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          rel="stylesheet"
          href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap"
        />
      </Helmet>

      <Box sx={{ bgcolor: "background.default", color: "text.primary", minHeight: "100vh" }}>
        <Link
          href="#main-content"
          sx={{
            bgcolor: "primary.main",
            borderRadius: 1,
            color: "common.white",
            left: 12,
            px: 2,
            py: 1,
            position: "fixed",
            top: -80,
            zIndex: 1400,
            "&:focus": { top: 12 }
          }}
        >
          Skip to content
        </Link>

        <Header />

        <Box id="main-content" component="main" tabIndex={-1}>
          <HeroSection onStartHiring={startHiring} onFindJobs={findJobs} />
          <AudienceSection onStartHiring={startHiring} onCandidateLogin={openCandidateLogin} />
          <FeaturesSection />
          <WorkflowSection />
          <ProductsSection />
          <PricingSection
            activeAudience={pricingAudience}
            onAudienceChange={setPricingAudience}
            onSelectPlan={openPlanInquiryDialog}
            pricing={selectedPricingModel}
          />
          <TestimonialsSection />
          <CtaSection onStartHiring={startHiring} />
        </Box>

        <Footer showDetailedFooter />

        <PlanInquiryDialog
          open={showPlanInquiryDialog}
          onClose={closePlanInquiryDialog}
          plan={selectedPlan}
          user={user}
          onSubmit={(payload) => {
            sendInquiryMail({
              name: payload.name,
              email: payload.email,
              plan: payload.planName
            });
          }}
        />

        <ResponseSnackbar
          open={response.open}
          message={response.message}
          severity={response.severity}
          onClose={() =>
            setResponse((current) => ({
              ...current,
              open: false
            }))
          }
        />
      </Box>
    </ThemeProvider>
  );
}
